/**
 * The site navigation, in one place: the header renders it, the footer mirrors
 * part of it, and the mobile panel is the same list at a different size.
 */

export type NavItem = {
  label: string
  href: string
  /**
   * Renders the item as a gradient-ringed pill instead of a plain link. The
   * desktop bar no longer has a slot for it (see `slot`), so today this only
   * marks the entry out inside the mobile panel.
   */
  highlight?: boolean
  /**
   * Where the entry sits on DESKTOP. The mobile panel prints the list exactly
   * as it stands whatever this says — a panel that hid an entry would be a
   * menu with a secret.
   *
   *  - omitted — a chip inside the bar's capsule.
   *  - `'icon'` — an icon button in the right-hand cluster instead of a chip.
   *    For an entry whose meaning survives being drawn rather than written.
   *  - `'menu'` — not in the bar at all, reachable from the panel and from the
   *    controls that already point at it.
   */
  slot?: 'icon' | 'menu'
}

export function navItems(locale: string): NavItem[] {
  // ── THE BAR IS THE OWNER'S COMP, LABEL FOR LABEL ────────────────────────
  // 2026-10-15, a Photoshop strip: „Strategia · Studio · Operator · Fotograf ·
  // Montaż · Grafika · Miniatury · Strony WWW", then a cart, a contact glyph
  // and one white button.
  //
  // It is the morning's order with the words cut back to one each. „Wynajem
  // studia" and „Wynajem sprzętu" were two tabs saying rental out loud, which
  // was the right fix for a bar of long labels and the wrong one for a bar
  // that now has nine chips inside a capsule: the two longest entries were
  // paying for a distinction the pages themselves make in their first line.
  //
  // The order runs with the service cards on the landing page — 01 Strategia
  // through 08 Strony — which is the one ladder a reader meets twice.
  return [
    { label: 'Strategia', href: `/${locale}/strategia` },
    { label: 'Studio', href: `/${locale}/studio` },
    // „Wynajem" IS IN THE BAR, owner's call on 16.10 after it had been taken
    // out to match the drawing: *„na pasku nawigacyjnym brakuje wynajmu"*.
    // The drawing has eight chips and this makes nine — the drawing is a
    // drawing, and the rental page is a live service with its own prices
    // that was reachable from every page except through the bar.
    { label: 'Wynajem', href: `/${locale}/wynajem` },
    // ONE page, TWO chips, and both light up on arrival. They used to be a
    // single entry whose label flipped between the two words, because a tab
    // cannot honestly be called either one — the comp answers that by giving
    // each its own chip, and the pair lit together says what the flip was
    // trying to say: this page is both of these people.
    { label: 'Operator', href: `/${locale}/operator` },
    { label: 'Fotograf', href: `/${locale}/fotograf` },
    { label: 'Montaż', href: `/${locale}/montaz` },
    { label: 'Grafika', href: `/${locale}/grafika` },
    { label: 'Miniatury', href: `/${locale}/miniaturki` },
    // NO PAGE EXISTS. The service is real — the owner wrote card 08's copy the
    // same day, naming strony, sklepy and aplikacje — but there is no
    // `/${locale}/strony`, no offering row and no price, so the chip lands on
    // the one screen that can actually take the enquiry. The moment the page
    // is built this href is the whole change.
    { label: 'Strony WWW', href: `/${locale}/kontakt` },
    // Drawn, not written: the comp replaces the Kontakt chip with a glyph next
    // to the cart. It keeps its full row in the mobile panel, where there is
    // no cluster to put it in and no room to be clever.
    { label: 'Kontakt', href: `/${locale}/kontakt`, slot: 'icon' },
    // Off the bar, by the comp. Everything in the right-hand cluster already
    // points into the order flow — the cart opens the creator's summary and
    // the white button opens the panel — so the chip was a third door onto a
    // corridor the bar already has two of.
    { label: 'Mój kreator', href: `/${locale}/kreator`, highlight: true, slot: 'menu' },
  ]
}

/**
 * Whether a nav entry points at the page being viewed. Exact match only: every
 * entry is a leaf, and prefix matching would light up "Wynajem" on any route
 * that happens to start with it.
 *
 * Two entries CAN be active at once — Operator and Fotograf share a page on
 * purpose. See the note on them above.
 */
export function isActive(pathname: string, href: string): boolean {
  return pathname === href
}
